/*
 * Hearth Court chat widget — vanilla JS, no dependencies, no build step.
 *
 * Config (set before this script loads, or leave the defaults):
 *   window.HC_CHAT_ENDPOINT — absolute URL of the /api/chat function.
 *
 * Notes for whoever edits this next:
 *  - Bot text is inserted with textContent, never innerHTML. The system prompt
 *    tells the model to answer in plain prose for exactly this reason. Do not
 *    "improve" this by rendering markdown without sanitizing.
 *  - Transcript lives in memory only. Nothing is persisted, nothing is sent
 *    anywhere but /api/chat. Closing the panel keeps the transcript; a reload
 *    clears it. That is deliberate — see the privacy line in the composer.
 */
(function () {
  "use strict";

  if (window.__hcChatLoaded) return;
  window.__hcChatLoaded = true;

  var ENDPOINT = window.HC_CHAT_ENDPOINT || "https://hearthcourt-chatbot.vercel.app/api/chat";
  var MAX_CHARS = 2000;

  var GREETING =
    "Hi — I'm Hearth Court's assistant. I can help with our fireplaces, " +
    "shipping and returns, and how ordering works. What can I help you with?";

  var OFFLINE =
    "I can't reach our system right now. Email support@hearthcourt.com or " +
    "call or text (307) 317-1030 and the team will help — we're in Monday " +
    "through Friday, 9 to 6 Eastern.";

  /** Conversation history in Claude API shape. */
  var history = [];
  var busy = false;

  // ------------------------------------------------------------------ DOM

  var root = document.createElement("div");
  root.className = "hc-chat";
  root.innerHTML = [
    '<div class="hc-chat__panel" role="dialog" aria-modal="false" aria-label="Hearth Court chat">',
    '  <div class="hc-chat__header">',
    "    <div>",
    '      <p class="hc-chat__title">Hearth Court</p>',
    '      <p class="hc-chat__subtitle">Mon–Fri, 9–6 ET</p>',
    "    </div>",
    '    <button type="button" class="hc-chat__close" aria-label="Close chat">×</button>',
    "  </div>",
    '  <div class="hc-chat__log" role="log" aria-live="polite" aria-atomic="false"></div>',
    '  <form class="hc-chat__form">',
    '    <textarea class="hc-chat__input" rows="1" placeholder="Ask about a fireplace, shipping, returns…"',
    '      aria-label="Your message" maxlength="' + MAX_CHARS + '"></textarea>',
    '    <button type="submit" class="hc-chat__send" aria-label="Send message">',
    '      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>',
    "    </button>",
    "  </form>",
    '  <p class="hc-chat__legal">Automated assistant. For safety, code, or installation questions we’ll connect you with a specialist.</p>',
    "</div>",
    '<button type="button" class="hc-chat__launcher" aria-label="Open chat with Hearth Court">',
    '  <svg viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round"',
    '    d="M21 11.5a8.4 8.4 0 01-9 8.4 9 9 0 01-3.9-.9L3 21l1.9-4.6A8.4 8.4 0 013 11.5a8.4 8.4 0 018.9-8.4 8.4 8.4 0 019.1 8.4z"/></svg>',
    "</button>",
  ].join("\n");

  var panel = root.querySelector(".hc-chat__panel");
  var log = root.querySelector(".hc-chat__log");
  var form = root.querySelector(".hc-chat__form");
  var input = root.querySelector(".hc-chat__input");
  var sendBtn = root.querySelector(".hc-chat__send");
  var launcher = root.querySelector(".hc-chat__launcher");
  var closeBtn = root.querySelector(".hc-chat__close");

  // ------------------------------------------------------------- rendering

  function bubble(text, kind) {
    var el = document.createElement("div");
    el.className = "hc-chat__msg hc-chat__msg--" + kind;
    el.textContent = text; // never innerHTML
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }

  function showTyping() {
    var el = document.createElement("div");
    el.className = "hc-chat__typing";
    el.setAttribute("aria-label", "Assistant is typing");
    el.innerHTML = "<span></span><span></span><span></span>";
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }

  function setBusy(state) {
    busy = state;
    sendBtn.disabled = state;
    input.disabled = state;
  }

  // ---------------------------------------------------------------- open

  var greeted = false;

  function open() {
    root.classList.add("hc-chat--open");
    if (!greeted) {
      bubble(GREETING, "bot");
      greeted = true;
    }
    setTimeout(function () { input.focus(); }, 50);
  }

  function close() {
    root.classList.remove("hc-chat--open");
    launcher.focus();
  }

  launcher.addEventListener("click", open);
  closeBtn.addEventListener("click", close);

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && root.classList.contains("hc-chat--open")) close();
  });

  // auto-grow the textarea; Enter sends, Shift+Enter newlines
  input.addEventListener("input", function () {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 96) + "px";
  });

  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  });

  // ---------------------------------------------------------------- send

  form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (busy) return;

    var text = input.value.trim();
    if (!text) return;

    bubble(text, "user");
    history.push({ role: "user", content: text });

    input.value = "";
    input.style.height = "auto";
    setBusy(true);
    var typing = showTyping();

    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: history }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (r) {
        typing.remove();
        if (r.ok && r.data && r.data.reply) {
          bubble(r.data.reply, "bot");
          history.push({ role: "assistant", content: r.data.reply });
        } else {
          // Drop the unanswered user turn so the next send starts from a
          // consistent history the API will accept.
          history.pop();
          bubble((r.data && r.data.error) || OFFLINE, "error");
        }
      })
      .catch(function () {
        typing.remove();
        history.pop();
        bubble(OFFLINE, "error");
      })
      .then(function () {
        setBusy(false);
        input.focus();
      });
  });

  // ---------------------------------------------------------------- mount

  function mount() {
    document.body.appendChild(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();
